/**
 * "Test stream" (ENG-2470): the shapes actuate_admin returns for a one-camera
 * stream test, opened from our side over the connector's own network path
 * (the address the customer allowlists, or their tunnel).
 */
import {
  CameraMedia,
  CameraStep,
  ConnectionPath,
  ConnectionPathContext,
  ConnectionPathRef,
  ConnectivityFinding,
  UrlKind,
} from "../SiteConnectivity/siteConnectivityTypes";

export type StreamTestStatus = "pending" | "running" | "done" | "failed";

export interface StreamTestResultDetail extends CameraMedia {
  ok: boolean;
  camera_status: string;
  reason_code: string | null;
  /** admin's one-line verdict, in the customer's words. */
  message: string;
  finding: ConnectivityFinding | null;
  /** Evidence from the connector, scrubbed of credentials. */
  detail: string | null;
  steps: CameraStep[];
  /** The tunnel / VPN the camera goes through; absent from older admin builds. */
  connection?: ConnectionPathRef | null;
  /** tunnel_down / vpn_down: that path and the site's other tunnels. */
  context?: ConnectionPathContext | null;
  codec: string | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  ms_to_first_frame: number | null;
  url_kind: UrlKind | string | null;
  /** A 10-minute presigned link to the frame we pulled. */
  snapshot_url: string | null;
  /** Older builds' expiry for the snapshot; newer ones send media_expires_at. */
  snapshot_expires_at: string | null;
}

export interface StreamTest {
  test_id: string;
  camera_id: number;
  camera_name: string | null;
  site_id: number;
  status: StreamTestStatus;
  created_at: string | null;
  checked_at: string | null;
  network_path: ConnectionPath | string | null;
  /** Why the test itself couldn't produce a result. */
  detail: string | null;
  /** Masked server-side: never holds the password. */
  stream_url_masked: string | null;
  result: StreamTestResultDetail | null;
}

export interface StreamTestStarted {
  test_id: string;
  status: "pending" | "failed";
  detail?: string | null;
}

/** Every answer a stream-test call can give, as a value the UI explains. */
export type StreamTestOutcome<T> =
  | { kind: "ok"; data: T }
  /**
   * 503 (or the route not deployed): the test is switched off, or the site
   * sends emails or partner events and has no stream (`siteType`).
   */
  | { kind: "unavailable"; siteType: boolean; detail: string | null }
  /** 400/409/429 refusals, by admin's `code`. */
  | {
      kind: "refused";
      code: string;
      detail: string | null;
      runId: string | null;
      retryAt: string | null;
    }
  | { kind: "not_found" }
  | { kind: "forbidden"; message: string }
  | { kind: "error"; message: string };

export const FINISHED_STREAM_TEST: ReadonlySet<StreamTestStatus> = new Set([
  "done",
  "failed",
]);
